import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Inbox, RefreshCw, AlertTriangle, Reply, Zap,
    BellOff, HelpCircle, ChevronLeft, ChevronRight,
    CheckCircle2, ClipboardList, Archive, Eye,
    Flag, Filter, Search, Mail,
    ArrowUpCircle, AlertCircle, MessageSquare
} from 'lucide-react';
import { api } from '../api/api';
import { SnackbarContext } from '../App';
import ThreadDetail from './ThreadDetail';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle,
    SheetDescription
} from './ui/sheet';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { cn } from '../lib/utils';
import { formatDate, formatRelativeDistance, normalizeApiError } from '../lib/i18n';

const CLASSIFICATION_META = {
    REPLY: { icon: Reply, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
    HARD_BOUNCE: { icon: AlertTriangle, color: 'text-red-400 bg-red-500/10 border-red-500/20' },
    SOFT_BOUNCE: { icon: AlertTriangle, color: 'text-orange-400 bg-orange-500/10 border-orange-500/20' },
    AUTO_REPLY: { icon: Zap, color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
    OUT_OF_OFFICE: { icon: BellOff, color: 'text-purple-400 bg-purple-500/10 border-purple-400/20' },
    UNKNOWN: { icon: HelpCircle, color: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20' }
};

const PRIORITY_META = {
    URGENT: { color: 'text-red-500', icon: AlertCircle },
    HIGH: { color: 'text-orange-500', icon: ArrowUpCircle },
    MEDIUM: { color: 'text-blue-500', icon: Flag },
    LOW: { color: 'text-zinc-500', icon: Flag },
};

export default function InboxPage() {
    const location = useLocation();
    const showSnackbar = useContext(SnackbarContext);
    const { t, i18n } = useTranslation();
    const [entries, setEntries] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 20 });

    // Filters State
    const [filters, setFilters] = useState({
        mailAccountId: 'ALL',
        classification: 'ALL',
        isRead: 'ALL',
        isArchived: 'false',
        priority: 'ALL',
        search: ''
    });

    // Selection State
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Detail & Action State
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchInbox = useCallback(async (page = 1) => {
        setLoading(true);
        setError('');
        try {
            const params = { page, limit: pagination.limit };
            if (filters.search) params.search = filters.search;
            if (filters.classification && filters.classification !== 'ALL') params.classification = filters.classification;
            if (filters.isRead && filters.isRead !== 'ALL') params.isRead = filters.isRead;
            if (filters.isArchived && filters.isArchived !== 'ALL') params.isArchived = filters.isArchived;
            if (filters.priority && filters.priority !== 'ALL') params.priority = filters.priority;
            if (filters.mailAccountId && filters.mailAccountId !== 'ALL') params.mailAccountId = filters.mailAccountId;

            const res = await api.get('/api/inbox/threads', { params });
            setEntries(res.data.data || []);
            setPagination(p => ({ ...p, ...res.data.pagination }));
            setSelectedIds(new Set());
        } catch (err) {
            setError(normalizeApiError(err, t, 'errors.loadInbox'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination.limit, t]);

    useEffect(() => {
        const loadAccounts = async () => {
            try {
                const res = await api.get('/api/mail-accounts');
                setAccounts(res.data?.accounts || []);
            } catch (err) {
                console.error('Failed to load accounts for inbox:', err);
            }
        };
        loadAccounts();
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const accId = searchParams.get('mailAccountId');
        if (accId) {
            setFilters(prev => ({ ...prev, mailAccountId: accId }));
        }
    }, [location.search]);

    useEffect(() => {
        fetchInbox(1);
    }, [filters, fetchInbox]);

    useEffect(() => {
        if (location.state?.dashboardFilters) {
            setFilters(prev => ({ ...prev, ...location.state.dashboardFilters }));
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const payload = filters.mailAccountId && filters.mailAccountId !== 'ALL' 
                ? { mailAccountId: filters.mailAccountId } 
                : {};
            const res = await api.post('/api/inbox/sync', payload);
            const stats = res.data?.data;
            const msg = stats?.processedMessages > 0
                ? (i18n.language === 'tr' 
                    ? `${stats.processedMessages} yeni mesaj senkronize edildi.` 
                    : `${stats.processedMessages} new message(s) synchronized.`)
                : (i18n.language === 'tr'
                    ? 'Senkronizasyon tamamlandı, yeni mesaj bulunamadı.'
                    : 'Sync complete. No new messages found.');
            showSnackbar(msg, 'success');
            await fetchInbox(pagination.currentPage);
        } catch (err) {
            showSnackbar(normalizeApiError(err, t, 'errors.syncFailed'), 'error');
            await fetchInbox(pagination.currentPage);
        } finally {
            setSyncing(false);
        }
    };

    const handleBulkAction = async (action, value) => {
        if (selectedIds.size === 0) return;
        setActionLoading(true);
        try {
            await api.patch('/api/inbox/bulk', {
                ids: Array.from(selectedIds),
                action,
                value
            });
            showSnackbar(t('inbox.bulkUpdated', { count: selectedIds.size }), 'success');
            fetchInbox(pagination.currentPage);
        } catch (err) {
            showSnackbar(normalizeApiError(err, t, 'errors.bulkAction'), 'error');
        } finally {
            setActionLoading(false);
        }
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === entries.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(entries.map(e => e._id)));
        }
    };

    const toggleSelect = (id, e) => {
        e.stopPropagation();
        const next = new Set(selectedIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        setSelectedIds(next);
    };

    const openDetails = async (thread) => {
        setSelectedEntry(null); // Clear previous
        setIsDrawerOpen(true);
        setActionLoading(true);
        try {
            const res = await api.get(`/api/inbox/threads/${thread._id}`);
            setSelectedEntry(res.data.data);
            // Mark all inbound messages in thread as read (optional but standard)
            if (!thread.isRead) {
                await api.patch('/api/inbox/bulk', { 
                    ids: res.data.data.messages.filter(m => m.type === 'INBOUND' && !m.isRead).map(m => m._id),
                    action: 'READ',
                    value: true
                });
                // Relaxed local state update
                setEntries(prev => prev.map(e => e._id === thread._id ? { ...e, isRead: true } : e));
            }
        } catch (err) {
            showSnackbar(t('inbox.detailsFailed'), 'error');
            setIsDrawerOpen(false);
        } finally {
            setActionLoading(false);
        }
    };

    const handleCreateTask = async (entry) => {
        setActionLoading(true);
        try {
            await api.post('/api/tasks', {
                title: `Review inbound: ${entry.subject || entry.context?.campaign?.name || t('nav.thread')}`,
                description: [
                    `Sender: ${entry.messages?.[0]?.from || entry.context?.contact?.email || t('common.unknown')}`,
                    entry.context?.campaign?.name ? `Campaign: ${entry.context.campaign.name}` : null,
                    entry.context?.sequence?.name ? `Sequence: ${entry.context.sequence.name}` : null,
                    entry.threadId ? `Thread: ${entry.threadId}` : null
                ].filter(Boolean).join('\n'),
                priority: entry.messages?.some(message => message.priority === 'URGENT') ? 'URGENT' : 'MEDIUM',
                workspaceContactId: entry.context?.contact?._id,
                sequenceAssignmentId: entry.context?.assignment?._id,
                sequenceNodeId: entry.context?.assignment?.currentNodeId,
                source: 'INBOX_THREAD'
            });
            showSnackbar(t('inbox.taskCreated'), 'success');
        } catch (err) {
            showSnackbar(err.message || 'Failed to create task', 'error');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <PageShell>
            <PageHeader
                title={t('inbox.title')}
                subtitle={t('inbox.subtitle', { count: pagination.totalRecords })}
                icon={Inbox}
                actions={
                    <div className="flex items-center gap-3">
                        <div className="relative group flex-1 sm:flex-none sm:w-64">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                            <Input
                                placeholder={t('inbox.searchPlaceholder')}
                                className="pl-11 h-12 text-sm font-semibold rounded-2xl bg-background border-2 border-border/50 focus:border-primary transition-all shadow-sm"
                                value={filters.search}
                                onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            />
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm" 
                            className="h-12 px-4 rounded-2xl shadow-sm border-2 border-border/50 hover:bg-muted transition-all bg-background gap-2 font-bold text-xs" 
                            onClick={handleSync}
                            disabled={syncing || loading}
                            title={t('inbox.syncTooltip', 'Postaları kontrol et')}
                        >
                            <RefreshCw className={cn("h-4 w-4", (syncing || loading) && "animate-spin text-primary")} />
                            <span>{syncing ? (i18n.language === 'tr' ? 'Kontrol Ediliyor...' : 'Syncing...') : (i18n.language === 'tr' ? 'Postaları Kontrol Et' : 'Sync Mail')}</span>
                        </Button>
                    </div>
                }
            />

            {/* Filter Bar */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide p-2">
                {/* Sender Account Filter */}
                <Select value={filters.mailAccountId} onValueChange={v => setFilters(p => ({ ...p, mailAccountId: v }))}>
                    <SelectTrigger className="w-[180px] h-8 text-[11px] font-bold rounded-full bg-accent/30 border-transparent">
                        <Mail className="h-3 w-3 mr-2 opacity-60 shrink-0 text-primary" />
                        <SelectValue placeholder={t('mailAccounts.all', 'Tüm Hesaplar')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">
                            <span className="font-semibold">{t('inbox.allAccounts', 'Tüm Hesaplar (All Accounts)')}</span>
                        </SelectItem>
                        {accounts.map((acc) => (
                            <SelectItem key={acc._id} value={acc._id}>
                                <div className="flex items-center gap-2 max-w-[220px]">
                                    <span className="truncate">{acc.user || acc.from}</span>
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={filters.isArchived} onValueChange={v => setFilters(p => ({ ...p, isArchived: v }))}>
                    <SelectTrigger className="w-[130px] h-8 text-[11px] font-bold uppercase rounded-full bg-accent/30 border-transparent">
                        <Archive className="h-3 w-3 mr-2 opacity-60" />
                        <SelectValue placeholder={t('common.status')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="false">{t('inbox.filters.activeInbox')}</SelectItem>
                        <SelectItem value="true">{t('inbox.filters.archived')}</SelectItem>
                        <SelectItem value="ALL">{t('inbox.filters.allMessages')}</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={filters.classification} onValueChange={v => setFilters(p => ({ ...p, classification: v }))}>
                    <SelectTrigger className="w-[140px] h-8 text-[11px] font-bold uppercase rounded-full bg-accent/30 border-transparent">
                        <Filter className="h-3 w-3 mr-2 opacity-60" />
                        <SelectValue placeholder={t('common.type')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">{t('inbox.filters.allTypes')}</SelectItem>
                        {Object.keys(CLASSIFICATION_META).map((k) => (
                            <SelectItem key={k} value={k}>{t(`inbox.classification.${k}`)}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>

                <Select value={filters.isRead} onValueChange={v => setFilters(p => ({ ...p, isRead: v }))}>
                    <SelectTrigger className="w-[110px] h-8 text-[11px] font-bold uppercase rounded-full bg-accent/30 border-transparent">
                        <Eye className="h-3 w-3 mr-2 opacity-60" />
                        <SelectValue placeholder={t('common.read')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">{t('common.all')}</SelectItem>
                        <SelectItem value="true">{t('status.read')}</SelectItem>
                        <SelectItem value="false">{t('status.unread')}</SelectItem>
                    </SelectContent>
                </Select>

                <Select value={filters.priority} onValueChange={v => setFilters(p => ({ ...p, priority: v }))}>
                    <SelectTrigger className="w-[120px] h-8 text-[11px] font-bold uppercase rounded-full bg-accent/30 border-transparent">
                        <Flag className="h-3 w-3 mr-2 opacity-60" />
                        <SelectValue placeholder={t('common.priority')} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL">{t('inbox.filters.allPriority')}</SelectItem>
                        <SelectItem value="URGENT">{t('inbox.filters.urgentOnly')}</SelectItem>
                        <SelectItem value="HIGH">{t('inbox.filters.highOnly')}</SelectItem>
                        <SelectItem value="MEDIUM">{t('inbox.priority.MEDIUM')}</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Bulk Action Bar */}
            {selectedIds.size > 0 && (
                <div className="flex items-center justify-between p-2 px-4 rounded-xl bg-primary text-primary-foreground animate-in slide-in-from-top-2 shadow-lg">
                    <div className="flex items-center gap-3">
                        <span className="text-xs font-bold uppercase tracking-widest">{t('common.selectedCount', { count: selectedIds.size })}</span>
                        <div className="w-px h-4 bg-primary-foreground/20" />
                        <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="h-8 gap-2 text-xs hover:bg-white/10" onClick={() => handleBulkAction('READ', true)}>
                                <Eye className="h-3.5 w-3.5" /> {t('actions.markRead')}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 gap-2 text-xs hover:bg-white/10" onClick={() => handleBulkAction('ARCHIVE', true)}>
                                <Archive className="h-3.5 w-3.5" /> {t('actions.archive')}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 gap-2 text-xs hover:bg-white/10" onClick={() => handleBulkAction('HANDLED', true)}>
                                <CheckCircle2 className="h-3.5 w-3.5" /> {t('actions.markHandled')}
                            </Button>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-8 gap-2 text-xs hover:bg-white/10">
                                        <Flag className="h-3.5 w-3.5" /> Priority
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent>
                                    {Object.entries(PRIORITY_META).map(([k, v]) => (
                                        <DropdownMenuItem key={k} onClick={() => handleBulkAction('PRIORITY', k)}>
                                            <v.icon className={cn("h-3.5 w-3.5 mr-2", v.color)} />
                                            {t(`inbox.priority.${k}`)}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => setSelectedIds(new Set())}>
                        <ArrowUpCircle className="h-4 w-4 rotate-45" />
                    </Button>
                </div>
            )}

            {/* Content Table */}
            <div className="flex-1 min-h-0 bg-card rounded-[2.5rem] border border-border shadow-sm overflow-hidden flex flex-col mt-6">
                <div className="overflow-x-auto flex-1 p-0.5">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="border-b border-border bg-muted/20 sticky top-0 z-10">
                                <th className="w-12 px-4 py-3">
                                    <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                                        checked={entries.length > 0 && selectedIds.size === entries.length}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">{t('inbox.table.information')}</th>
                                <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">{t('inbox.table.sender')}</th>
                                <th className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">{t('inbox.table.context')}</th>
                                <th className="px-4 py-3 text-right text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-60">{t('inbox.table.receivedAt')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/50">
                            {loading && entries.length === 0 ? (
                                Array.from({ length: 8 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td className="p-4" colSpan="5"><div className="h-12 bg-muted/40 rounded-xl" /></td>
                                    </tr>
                                ))
                            ) : error ? (
                                <tr>
                                    <td colSpan="5" className="p-20 text-center">
                                        <div className="flex flex-col items-center gap-4 text-muted-foreground">
                                            <div className="h-12 w-12 rounded-full bg-destructive/10 flex items-center justify-center">
                                                <AlertTriangle className="h-6 w-6 text-destructive/60" />
                                            </div>
                                            <p className="font-medium text-foreground">{error}</p>
                                            <Button variant="outline" size="sm" onClick={() => fetchInbox(1)}>{t('inbox.reconnect')}</Button>
                                        </div>
                                    </td>
                                </tr>
                            ) : entries.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="p-20 text-center">
                                        <div className="flex flex-col items-center gap-4 text-muted-foreground">
                                            <div className="h-16 w-16 rounded-full bg-muted/30 flex items-center justify-center">
                                                <Inbox className="h-8 w-8 opacity-20" />
                                            </div>
                                            <div className="max-w-xs">
                                                <p className="font-bold text-foreground mb-1">{t('inbox.emptyTitle')}</p>
                                                <p className="text-xs opacity-60">{t('inbox.emptyDescription')}</p>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                             ) : entries.map((thread) => (
                                <tr
                                    key={thread._id}
                                    className={cn(
                                        "group border-b border-border/40 transition-all cursor-pointer hover:bg-muted/30",
                                        !thread.isRead && "bg-primary/[0.02]",
                                        selectedIds.has(thread._id) && "bg-primary/5 hover:bg-primary/10"
                                    )}
                                    onClick={() => openDetails(thread)}
                                >
                                    <td className="px-4 py-3" onClick={e => toggleSelect(thread._id, e)}>
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                                            checked={selectedIds.has(thread._id)}
                                            readOnly
                                        />
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-2">
                                            <div className="flex items-center gap-2">
                                                {!thread.isRead && (
                                                    <div className="h-2 w-2 rounded-full bg-primary animate-pulse shadow-[0_0_8px_rgba(59,130,246,0.5)]" />
                                                )}
                                                <Badge variant="outline" className="text-[9px] uppercase font-bold py-0 h-5">
                                                    {t('common.messagesCount', { count: thread.count })}
                                                </Badge>
                                            </div>
                                            <div className="flex flex-col">
                                                <span className={cn("text-sm line-clamp-1 transition-colors", !thread.isRead ? "font-bold text-foreground" : "text-muted-foreground group-hover:text-foreground")}>
                                                    {thread.subject || t('common.noSubject')}
                                                </span>
                                                <p className="text-xs text-muted-foreground/60 line-clamp-1 italic">{thread.latestSnippet}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-full bg-accent/40 border border-border flex items-center justify-center shrink-0 uppercase text-[10px] font-black text-muted-foreground">
                                                {(thread.from || '??').replace(/^[^a-zA-Z0-9]+/, '').substring(0, 2).toUpperCase() || '??'}
                                            </div>
                                            <div className="flex flex-col min-w-0 max-w-[150px]">
                                                <span className="text-sm font-semibold truncate text-foreground">{(thread.from || 'Unknown').split('@')[0]}</span>
                                                {thread.from && thread.from.includes('@') && (
                                                    <span className="text-[10px] text-muted-foreground font-medium truncate opacity-60">@{thread.from.split('@')[1]}</span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex flex-col gap-1.5">
                                            {thread.mailAccount?.user || thread.to ? (
                                                <div className="flex items-center gap-1.5" title={`Received by: ${thread.mailAccount?.user || thread.to}`}>
                                                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">To:</span>
                                                    <Badge variant="secondary" className="text-[11px] font-semibold py-0.5 px-2 bg-accent/40 hover:bg-accent/60 text-foreground max-w-[200px] truncate">
                                                        <Mail className="w-3 h-3 mr-1 text-primary shrink-0 inline" />
                                                        <span className="truncate">{thread.mailAccount?.user || thread.to}</span>
                                                    </Badge>
                                                </div>
                                            ) : (
                                                <span className="text-[10px] text-muted-foreground italic opacity-50">{t('inbox.contextResolution')}</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex flex-col items-end">
                                            <span className="text-[11px] font-bold text-muted-foreground">
                                                {formatRelativeDistance(thread.latestReceivedAt, {}, i18n.language)}
                                            </span>
                                            <span className="text-[9px] text-muted-foreground opacity-40 uppercase tracking-widest mt-0.5">
                                                {formatDate(thread.latestReceivedAt, { hour: '2-digit', minute: '2-digit' }, i18n.language)}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Stats */}
                <div className="p-3 border-t border-border bg-muted/10 flex items-center justify-between">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">
                        {t('inbox.displaying', { count: entries.length, total: pagination.totalRecords })}
                    </p>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="ghost" size="icon" className="h-8 w-8 rounded-lg"
                            disabled={pagination.currentPage === 1}
                            onClick={() => fetchInbox(pagination.currentPage - 1)}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span className="text-xs font-black min-w-[2rem] text-center">{pagination.currentPage} / {pagination.totalPages}</span>
                        <Button
                            variant="ghost" size="icon" className="h-8 w-8 rounded-lg"
                            disabled={pagination.currentPage === pagination.totalPages}
                            onClick={() => fetchInbox(pagination.currentPage + 1)}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Thread Details Drawer */}
            <Sheet open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
                <SheetContent className="sm:max-w-[100vw] md:max-w-5xl overflow-hidden w-full p-0 flex flex-col border-l shadow-2xl">
                    <SheetHeader className="p-6 border-b shrink-0 flex flex-row items-center justify-between space-y-0">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                <MessageSquare className="h-5 w-5 text-primary" />
                            </div>
                            <div>
                                <SheetTitle className="text-lg font-bold">{t('inbox.conversationHistory')}</SheetTitle>
                                <SheetDescription className="text-xs">{t('inbox.threadId')}: {selectedEntry?.threadId?.substring(0, 16)}...</SheetDescription>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 pr-8">
                            {selectedEntry && (
                                <Button variant="outline" size="sm" onClick={() => handleCreateTask(selectedEntry)}>
                                    <ClipboardList className="w-4 h-4 mr-2" /> {t('actions.createTask')}
                                </Button>
                            )}
                        </div>
                    </SheetHeader>
                    {actionLoading && !selectedEntry ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="flex flex-col items-center gap-3">
                                <RefreshCw className="h-8 w-8 text-primary animate-spin opacity-20" />
                                <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground animate-pulse">{t('inbox.syncingConversation')}</p>
                            </div>
                        </div>
                    ) : selectedEntry ? (
                        <ThreadDetail 
                            threadData={selectedEntry} 
                            onReplySent={() => {
                                showSnackbar(t('inbox.manualReplySent'), 'success');
                                openDetails({ _id: selectedEntry.threadId, isRead: true }); // Refresh
                                fetchInbox(pagination.currentPage);
                            }}
                            onActionCompleted={() => {
                                showSnackbar(t('inbox.actionSyncSuccess'), 'success');
                                openDetails({ _id: selectedEntry.threadId, isRead: true });
                            }}
                        />
                    ) : null}
                </SheetContent>
            </Sheet>
        </PageShell>
    );
}
